import { TaskResponse } from "model/api/tasks/queries";

import { LeadAction } from "domain/shared/types/lead-action";

import { GENERATE_COVER_LETTER_FORM, NEED_ANALYSIS_FORM, INSURANCE_FORM, MILEAGE_CONFIRMATION_FORM, PAYOFF_ID_FORM, SOLD_CARD_FORM } from "domain/shared/screens";

export enum Status {
  PENDING = 'pending',
  COMPLETED = 'completed',
  NO_APPLICABLE = 'no_applicable',
  IN_PROGRESS = 'in_progress',
}

export enum Type {
  DEFAULT,
  CAMERA,
  FORM,
  LIST,
  PULL_CREDIT,
  COVER_LETTER,
  OFAC,
  ID_VERIFICATION,
  TURNAUTO,
  SHARABLE_FORM,
}

export type OptionDoc = {
  id: number;
  name: string;
}

type Config = {
  slug: string,
  
  item?: string,
  screen?: string;
  optional?: boolean;
  optionalMessage?: string;
  cobuyerSignDocs?: boolean;
  optionsDocs?: OptionDoc[];
  direct?: boolean;
  messageUuid?: string;
  iframe_url_signed?: string;
  disabled: boolean;
  inactive: boolean;
  hide?: boolean;

  action: LeadAction;
  options?: LeadAction[];
}


const FORMS = ['credit-app', 'co-signer', 'payoff-verification', 'insurance-verification', 'sold-car-verification']
const LIST = ['get-docs', 'cc-auth', 'csi-approval', 'credit-consent', 'purchase-vehicle', 'esign-docs'];

export default class Task {
  constructor(
    readonly id: number,
    readonly title: string,
    readonly type: Type,
    readonly status: Status,
    readonly config: Config,
  ) {
  }

  static fromGraph(task: TaskResponse, userRoles: string[] = []) {
    const { name, description, form_config } = task.company_action;
    const { slug } = task.company_action.action;

    const configuration = task.config || {};
    const type = Task.getType(slug, configuration);
    const status = Task.getStatus(task.status);
    const screen = Task.getScreen(slug);

    let {
      id: item = undefined,
      options = undefined,
      optionsDocs = undefined,
      disabled = false,
      inactive = false,
      managerOnly = false,
    } = configuration;

    const optional = !!task.config?.optional_required_message || ['co-signer', 'add-trade', 'payoff-verification'].includes(slug);
    const direct = ['esign-docs'].includes(slug);

    if (slug === 'upload-docs' && item === undefined) item = 'score-disclosure';

    const action = {
      ...task.company_action.action,
      id: task.company_action.id,
      name,
      description,
      form_config,
    };

    const config = {
      slug,
      action,
      item,
      options,
      optional,
      optionalMessage: task.config?.optional_required_message,
      optionsDocs,
      direct,
      screen,
      messageUuid: task.engagement_end?.message.uuid ?? undefined,
      cobuyerSignDocs: task.config?.cobuyer_dl ?? false,
      iframe_url_signed: task.engagement_end?.message?.message?.data?.iframe_url_digital_jacket ?? undefined,
      disabled,
      inactive,
      hide: managerOnly && !userRoles.includes("Manager")
    };

    return new Task(task.id, task.name, type, status, config);
  }

  private static getType(slug: string, config: any): Type {
    if (slug === 'generate-cover-letter') return Type.COVER_LETTER;
    if (slug === 'upload-docs') return Type.CAMERA;
    if (slug === '700-credit') return Type.PULL_CREDIT;
    if (FORMS.includes(slug)) return Type.FORM;
    if (LIST.includes(slug)) return Type.LIST;
    if (slug === 'needs-analysis') return config['sharable-form'] ? Type.SHARABLE_FORM : Type.DEFAULT;

    if (slug === 'ofac') return Type.OFAC;
    if (slug === 'id-verification') return Type.ID_VERIFICATION;
    if (slug === 'turnauto') return Type.TURNAUTO;

    return Type.DEFAULT;
  }

  private static getStatus(status: string): Status {
    if (status === 'completed') return Status.COMPLETED;
    if (status === 'no_applicable') return Status.NO_APPLICABLE;
    return Status.PENDING
  }

  private static getScreen(slug: string): string | undefined {
    const strategy: Record<string, string | undefined> = {
      'payoff-verification': PAYOFF_ID_FORM,
      'insurance-verification': INSURANCE_FORM,
      'sold-car-verification': SOLD_CARD_FORM,
      'mileage-confirmation': MILEAGE_CONFIRMATION_FORM,
      'generate-cover-letter': GENERATE_COVER_LETTER_FORM,
      'needs-analysis': NEED_ANALYSIS_FORM,
      'default': undefined
    };

    return strategy[slug] || strategy.default;
  }
}